import { trigger } from "@angular/animations";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { Store } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { skip, take, takeUntil } from "rxjs/operators";

import { ModalData } from "@serasa/core/modules/users/components/user-profile-change/models/modal-data.mode";
import { ModalOptions } from "@serasa/core/modules/users/components/user-profile-change/models/modal-options.model";
import { UserProfileChangeIncompleteComponent } from "@serasa/core/modules/users/components/user-profile-change/user-incomplete/user-profile-change-incomplete.component";
import { fadeIn } from "@serasa/shared/animations/fadeIn";
import { BlockedReasonsEnum } from "@serasa/shared/enums/blocked-reasons.enum";
import { SiteSection } from "@serasa/shared/enums/site-section.enum";
import { UserProfileEnum,  UserProfileDescription} from "@serasa/shared/enums/user-profile.enum";
import { UserStatusEnum } from "@serasa/shared/enums/user-status.enum";
import { MfaStatusEnum, StatusMfaDescription } from "@serasa/shared/enums/mfa-status.enum";
import { AnalyticsService } from "@serasa/shared/services/analytics.service";
import { SidenavManagerService } from "@serasa/shared/services/sidenav.manager.service";
import { Utils } from "@serasa/shared/utils/utils";
import { SedsModalService } from "experian-design-system";
import { Logon } from "../../../users/interfaces/logon.interface";
import { PageInfo } from "../../../users/interfaces/page-info.interface";
import { IBlockageTypes, User } from "../../../users/interfaces/user.interface";
import { UserService } from "../../../users/services/user.service";
import {
  loadLogonsList,
  loadSelectedLogon,
  loadToggleSidenav,
} from "../../../users/state/actions/user.actions";
import {
  selectEmptyList,
  selectErrorListing,
  selectIsLoadingSkeleton,
  selectLogonsList,
  selectPageOptions,
} from "../../../users/state/selectors/user.selectors";
import { BlockUserComponent } from "../block-user/block-user.component";
import { DeleteUserComponent } from "../delete-user/delete-user.component";
import { ErrorLoadingModalComponent } from "../error-loading-modal/error-loading-modal.component";
import { ResetPasswordComponent } from "../reset-password/reset-password.component";
import { UnblockUserComponent } from "../unblock-user/unblock-user.component";

@Component({
    selector: "pl-listing",
    templateUrl: "./listing.component.html",
    styleUrls: ["listing.component.scss"],
    animations: [trigger("fadeIn", fadeIn())],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ListingComponent implements OnInit, OnDestroy {
  public readonly PAGE_SIZE = 20;
  public pageEvent: PageEvent = {
    pageIndex: 0,
    pageSize: this.PAGE_SIZE,
    length: 0,
  };
  public tableCols: string[] = [
    "name",
    "logon",
    "email",
    "role",
    "status",
    "mfaStatus",
    "actions",
  ];

  public currentUserId?: number | string;
  public error$!: Observable<HttpErrorResponse | null | undefined>;
  public isLoading$!: Observable<boolean>;
  public logons$!: Observable<Logon>;
  public logonsList$!: Observable<any>;
  public pageOptions$!: Observable<any>;
  public selectedLogon!: Logon;
  public showEmptyList$!: Observable<boolean>;
  public tableDataSrc = new MatTableDataSource();
  public updated = false;
  public userProfileEnum: any = UserProfileEnum;
  public userProfileDescription: any = UserProfileDescription;
  public userStatusEnum: any = UserStatusEnum;
  public mfaStatusEnum: any = MfaStatusEnum;
  public statusMfaDescription: any = StatusMfaDescription;
  public users: User[] = [];

  public logonTypeLoggedProfileIsUser = false;

  private readonly _destroy$ = new Subject();

  constructor(
    private readonly _sidenavManagerService: SidenavManagerService,
    private readonly _usersService: UserService,
    private readonly _dialog: SedsModalService,
    private readonly _analyticsService: AnalyticsService,
    private readonly store: Store,
  ) {
    this.error$ = this.store.select(selectErrorListing);
    this.isLoading$ = this.store.select(selectIsLoadingSkeleton);
    this.logonsList$ = this.store.select(selectLogonsList);
    this.pageOptions$ = this.store.select(selectPageOptions);
    this.showEmptyList$ = this.store.select(selectEmptyList);
  }
  ngOnInit(): void {
    this._initSubscribers();
    this._initLoggedUserInfo();

    this.store.dispatch(
      loadLogonsList({
        offset: 0,
        limit: this.PAGE_SIZE,
        isPagination: false,
      }),
    );
  }

  private _initLoggedUserInfo(): void {
    const profile = Utils.verifyUserProfile();
    this.logonTypeLoggedProfileIsUser = profile === UserProfileEnum.USER;
  }

  public handlePageEvent(pageEvent: PageEvent): void {
    this.pageEvent = pageEvent;
    const { pageIndex, pageSize } = pageEvent;

    localStorage.setItem("mat-paginator", JSON.stringify(pageEvent));

    this.store.dispatch(
      loadLogonsList({
        offset: pageIndex,
        limit: pageSize,
        isPagination: true,
      }),
    );
  }

  public view(user: User): void {
    this._navigateTo(user, "");
  }

  public edit(user: User): void {
    this._navigateTo(user, "edit");
  }

  public changeProfile(user: User): void {
    if (!user?.userId) {
      this._showErrorLoadingModal();
      return;
    }

    this._verifyUserData(user);
  }

  private _hasIncompleteProfile(user: User): boolean {
    const requiredFields: Array<keyof User> = ['email', 'logon', 'name', 'profile', 'userId'];
    return !user || !requiredFields.every(field => !!user[field]);
  }

  private _showIncompleteProfileModal(user: User): void {
    const modalData: ModalData = {
      title: 'Usuário com cadastro incompleto',
      content: "O cadastro do usuário está incompleto (nome, e-mail e/ou CPF ausentes). Preencha essas informações para alterar o perfil para 'Ponto focal'.",
    };

    const options: ModalOptions = {
      width: "550px",
    };

    this._dialog.open(UserProfileChangeIncompleteComponent, modalData, options);
    this._dialog.close().subscribe(result => {
      if (result === 'confirmed') {
        this._navigateTo(user, "edit");
      }
    });
  }

  private _navigateTo(user: User, destination: string): void {
    this._setCurrentUser(user);
    this._openSidenav();
    this._sidenavManagerService.navigate$.next(destination);
  }

  public startResetFlow(user: User): void {
    this._setCurrentUser(user);
    this._dialog.open(ResetPasswordComponent, null, {width: '513px'});
  }

  public blockUser(user: User): void {
    this._setCurrentUser(user);
    this._dialog.open(BlockUserComponent, null, {width: '513px'});
  }

  public unblockUser(user: User): void {
    this._setCurrentUser(user);
    this._dialog.open(UnblockUserComponent, null, {width: '513px'});
  }

  public deleteUser(user: User): void {
    this._setCurrentUser(user);
    this._dialog.open(DeleteUserComponent, null,{
      minHeight: "322px",
      width: '513px'
    });
  }

  public formatBlockingReasons(
    blockages: IBlockageTypes[],
    userStatus: string,
  ): string {
    if (blockages?.length && userStatus !== "INACTIVE") {
      const blockageDescriptions: string[] = [];
      for (const blockage of blockages) {
        blockageDescriptions.push(
          Utils.translateReason(blockage.description as keyof typeof BlockedReasonsEnum),
        );
      }

      return blockageDescriptions?.join(" / ");
    }

    return this._verifyLabelForUserStatus(userStatus);
  }

  public canUserSeeUnblockedOption(user: User): boolean {
    return Utils.canUserSeeUnblockedOption(user);
  }

  public isSameUser(user: Partial<User>): boolean {
    const userLogon = Utils.decodeToken(Utils.getCookie("accessToken"));
    return userLogon?.logon === user.logon;
  }

  private _verifyLabelForUserStatus(userStatus: string): string {
    const newUserStatus = userStatus.toUpperCase();
    const tooltipStatusWithMessages: { [key: string]: string } = {
      ACTIVE: "A conta está ativa e pode acessar todos os produtos disponíveis em seu acesso.",
      PENDING_UPDATE: "A conta está pendente por cadastro incompleto ou dados faltantes.",
      NOT_FOUND: "A conta foi removida e não está mais disponível para uso.",
      INACTIVE: "A conta está pendente por cadastro incompleto ou dados faltantes.",
      DELETED: "A conta foi removida e não está mais disponível para uso.",
    };

    return tooltipStatusWithMessages[newUserStatus];
  }

  private _openSidenav() {
    this.store.dispatch(
      loadToggleSidenav({
        openSidenav: true,
      }),
    );
  }

  private _setCurrentUser(user: Partial<User>) {
    this.store.dispatch(
      loadSelectedLogon({
        selectedUserId: user?.userId || "",
        userDataTable: user,
      }),
    );
  }

  private _initSubscribers(): void {
    this._usersService.updated$
      .pipe(takeUntil(this._destroy$))
      .subscribe((flag: boolean) => {
        if (flag) {
          this.currentUserId = this._usersService.currentUser$.value.id || 0;
          this._usersService.currentUser$.next({});
          this.updated = false;
        }
      });

    this.pageOptions$
      .pipe(takeUntil(this._destroy$))
      .subscribe((s: PageInfo) => {
        this.pageEvent.length = s.totalElements;
      });

    this.error$
      .pipe(takeUntil(this._destroy$))
      .subscribe((error: HttpErrorResponse | undefined | null) => {
        if (error) {
          this._analyticsService.sendCustomError(
            error,
            SiteSection.ERRO_LISTAGEM_USUARIOS,
          );
        }
      });

    this._dispatchUserListDatalayer();
  }

  private _dispatchUserListDatalayer() {
    this.logonsList$
      .pipe(skip(1), take(1), takeUntil(this._destroy$))
      .subscribe((logons: User[]) => {
        Utils.dispatchUserList(this._analyticsService);
      });
  }

  private _showErrorLoadingModal(title = 'Não foi possível carregar as informações'): void {
    const modalData: ModalData = {
      title: title,
    };

    const options: ModalOptions = {
      width: "550px",
    };

    this._dialog.open(ErrorLoadingModalComponent, modalData, options);
  }

  private _verifyUserData(user: User): void {
    if (!user.userId) {
      this._showErrorLoadingModal();
      return;
    }

    this._usersService.getUserAccount(user.userId)
      .subscribe({
        next: (userData) => {
          const updatedUser: User = {
            ...user,
            email: userData.email || user.email,
            name: userData.fullname || user.name,
            logon: userData.logon || user.logon,
            userId: userData.userId || user.userId,
            profile: userData.profile || user.profile
          };

          if (this._hasIncompleteProfile(updatedUser)) {
            this._showIncompleteProfileModal(updatedUser);
          } else {
            this._navigateTo(updatedUser, "change-profile");
          }
        },
        error: (error) => {
          this._showErrorLoadingModal('Não foi possível carregar as informações');
        }
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next("");
    this._destroy$.complete();
  }
}
